<template>
  <div class="rail">
    <div class="rail__head">
      <app-brand-mark :size="22" />
    </div>

    <!-- Liste und „Seitenleiste ausklappen“ scrollen gemeinsam; der Knopf steht unten (margin-top: auto) -->
    <div class="rail__scroll">
      <nav class="rail__nav" aria-label="Hauptnavigation">
        <ul class="rail__list">
          <li v-for="group in groups" :key="group.id" class="rail__entry">
            <template v-if="!group.children">
              <router-link
                :ref="'link-' + group.id"
                :to="group.path"
                class="rail__item"
                :class="{ 'rail__item--here': isActivePath(group.path) }"
                :aria-label="group.title"
                :aria-current="isActivePath(group.path) ? 'page' : null"
              >
                <app-icon :name="group.icon" :size="18" />
              </router-link>
              <v-tooltip
                v-if="tipsOn"
                :activator="linkEl(group.id)"
                right
                open-delay="400"
                content-class="app-tooltip"
              >
                <span>{{ group.title }}</span>
              </v-tooltip>
            </template>
            <app-nav-flyout v-else :group="group" :active-path="activePath" tooltip>
              <template v-slot:activator="{ on, attrs }">
                <button
                  type="button"
                  class="rail__item"
                  :class="{ 'rail__item--here': group.id === activeGroupId }"
                  v-bind="attrs"
                  :aria-label="group.title"
                  :aria-current="group.id === activeGroupId ? 'true' : null"
                  v-on="on"
                >
                  <app-icon :name="group.icon" :size="18" />
                </button>
              </template>
            </app-nav-flyout>
          </li>
        </ul>
      </nav>

      <div v-if="canExpand" class="rail__system">
        <app-icon-button
          icon="panel"
          label="Seitenleiste ausklappen"
          :size="32"
          :boxed="false"
          class="rail__toggle"
          :tooltip="$shell.popupOffen ? false : 'right'"
          data-shell-toggle
          @click="expand"
        />
      </div>
    </div>

    <div class="rail__foot">
      <app-user-menu placement="right" :nudge="14" @sign-out="$emit('sign-out')">
        <template v-slot:activator="{ on, attrs }">
          <button
            ref="user"
            type="button"
            class="rail__user"
            v-bind="attrs"
            aria-haspopup="menu"
            :aria-label="'Benutzermenü von ' + (userTitle || 'Benutzer')"
            v-on="on"
          >
            <app-avatar :name="name" :size="28" ring />
          </button>
        </template>
      </app-user-menu>
      <v-tooltip v-if="tipsOn" :activator="userEl()" right open-delay="400" content-class="app-tooltip">
        <span>{{ userTitle }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
// Rail 60 (Maus; Seitenleiste eingeklappt oder Breite 600–1263; Spezifikation 2.11, gen_r3.py:833–866):
// Punkte 32 × 32 nur mit Icon (Abstand 36 wie in der Seitenleiste, Icon-Mitte 30), Tooltip rechts (nur Maus, nicht
// bei offenem Popup). Übersicht ist ein Link, jede Gruppe öffnet per Klick ihr Flyout (AppNavFlyout). Oben der
// SIMA-Stern; Liste und „Seitenleiste ausklappen“ (nur ab 1264) scrollen gemeinsam; unten der Avatar-Knopf mit
// Benutzermenü und Tooltip „Name · Benutzername“. Kopfzeile und Profil fluchten wie in der Seitenleiste.
import AppNavFlyout from './AppNavFlyout.vue';
import AppBrandMark from './AppBrandMark.vue';
import AppUserMenu from './AppUserMenu.vue';
import { normalizePath, userDisplayName, userLoginName } from '../../shell/navigation';
import { setSidebarCollapsed } from '../../shell/shellState';

export default {
  name: 'AppRail',
  components: { AppBrandMark, AppNavFlyout, AppUserMenu },
  props: {
    groups: { type: Array, required: true },
    activePath: { type: String, default: '' },
    activeGroupId: { type: String, default: null },
    // „Seitenleiste ausklappen“ nur ab 1264
    canExpand: { type: Boolean, default: false }
  },
  data() {
    return { mounted: false };
  },
  computed: {
    name() {
      return userDisplayName(this.$store.state.profile);
    },
    // wie der Benutzerknopf der Seitenleiste: Name und Benutzername
    userTitle() {
      return [this.name, userLoginName(this.$store.state.profile)].filter(Boolean).join(' · ');
    },
    tipsOn() {
      return this.mounted && !(this.$dichte && this.$dichte.touch) && !this.$shell.popupOffen;
    }
  },
  mounted() {
    this.mounted = true;
  },
  methods: {
    isActivePath(path) {
      return normalizePath(path) === this.activePath;
    },
    // Element des Hauptpunkt-Links (Tooltip über die activator-Prop; router-link reicht keine Maus-Ereignisse durch)
    linkEl(id) {
      const ref = this.$refs['link-' + id];
      const link = Array.isArray(ref) ? ref[0] : ref;
      return link && link.$el ? link.$el : null;
    },
    userEl() {
      return this.$refs.user || null;
    },
    expand(event) {
      setSidebarCollapsed(false);
      // per Tastatur ausgelöst (detail 0): Fokus auf „Seitenleiste einklappen“ der Seitenleiste
      if (event && event.detail === 0) {
        this.$nextTick(() => {
          const toggle = document.querySelector('.shell-nav [data-shell-toggle]');
          if (toggle) {
            toggle.focus();
          }
        });
      }
    }
  }
};
</script>

<style scoped>
/* feste Breite: der Inhalt bleibt stehen, während Vuetify die Breite des Drawers animiert. Oben 21 wie die
   Seitenleiste (Kopfzeile auf der Zeile des Seitenkopfs); unten 24: der Avatar (28 im Knopf 32) endet 26 über der
   Fensterkante, so wie der Stern oben 26 darunter beginnt. */
.rail {
  width: 60px;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 21px 0 24px;
  font-family: var(--font);
}
.rail__head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  margin-bottom: 12px;
}
/* Liste und Ausklappen in einem Scrollbereich mit Scrollschatten (nur wenn Inhalt verdeckt ist); Innenraum für
   Fokusringe */
.rail__scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin: -4px 0;
  padding: 4px 0;
  background:
    linear-gradient(var(--chrome) 30%, transparent) top / 100% 24px no-repeat local,
    linear-gradient(transparent, var(--chrome) 70%) bottom / 100% 24px no-repeat local,
    radial-gradient(farthest-side at 50% 0, var(--sh-scroll), transparent) top / 100% 8px no-repeat scroll,
    radial-gradient(farthest-side at 50% 100%, var(--sh-scroll), transparent) bottom / 100% 8px no-repeat scroll;
  background-color: var(--chrome);
}
.rail__nav {
  flex: none;
}
.rail__list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}
.rail__entry {
  display: flex;
}
.rail__item {
  width: 32px;
  height: 32px;
  flex: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 1px solid transparent;
  border-radius: var(--r-ctl);
  background: transparent;
  color: var(--ink-3);
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .rail__item:hover,
.rail__item[aria-expanded="true"] {
  background: var(--seg-hov);
}
/* aktiv: graue Fläche (--here) ohne Kante und Schatten (der Ort, kein Knopf), Icon orange */
.rail__item.rail__item--here {
  background: var(--here);
  color: var(--acc);
}
.rail__item:active {
  background: var(--seg-press);
}

/* „Seitenleiste ausklappen“ unten in der Liste (nur ab 1264) */
.rail__system {
  flex: none;
  margin-top: auto;
  display: flex;
  justify-content: center;
}
.rail__system .rail__toggle {
  color: var(--ink-3);
}
:where(html:not(.dichte-touch)) .rail__system .rail__toggle:not(:disabled):hover {
  background: var(--seg-hov);
  color: var(--ink);
}
.rail__system .rail__toggle:not(:disabled):active {
  background: var(--seg-press);
}

/* Benutzerblock ohne Trennlinie */
.rail__foot {
  flex: none;
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.rail__user {
  width: 32px;
  height: 32px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  font-family: var(--font);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .rail__user:hover,
.rail__user[aria-expanded="true"] {
  background: var(--seg-hov);
}
.rail__user:active {
  background: var(--seg-press);
}
</style>
